// jmix-3b.2: CSRF priming for the cookie-bearing /api/auth/refresh + /api/auth/logout calls. Those
// chains use a double-submit XSRF-TOKEN cookie, but on a cold load the SPA has none yet, so the first
// call would 403. `ensureCsrfToken` GETs /api/auth/csrf once: the API sets the XSRF-TOKEN cookie AND
// returns the raw token, which we cache in memory and echo as the X-XSRF-TOKEN header (the refresh
// chain uses the plain request handler, so it expects the raw cookie value verbatim).

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

let cachedToken: string | undefined;

/** Fetch + cache the CSRF token (and let the browser store the XSRF-TOKEN cookie). Idempotent. */
export async function ensureCsrfToken(): Promise<string | undefined> {
  if (cachedToken) {
    return cachedToken;
  }
  try {
    const response = await fetch(`${API_URL}/auth/csrf`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    });
    if (!response.ok) {
      return undefined;
    }
    const body = (await response.json().catch(() => null)) as { token?: string } | null;
    cachedToken = body?.token ?? undefined;
    return cachedToken;
  } catch {
    return undefined;
  }
}

/** Headers carrying the CSRF token for a cookie-bearing write (empty if priming failed). */
export async function csrfHeaders(): Promise<Record<string, string>> {
  const token = await ensureCsrfToken();
  return token ? { "X-XSRF-TOKEN": token } : {};
}

/** Drop the cached token (e.g. after a 403) so the next call re-primes. */
export function clearCsrfToken(): void {
  cachedToken = undefined;
}
